import DOMPurify from 'dompurify';

// Add hook to sanitize dangerous CSS URLs - remove entire attribute if dangerous
DOMPurify.addHook('afterSanitizeAttributes', (currentNode) => {
  if (currentNode.hasAttribute && currentNode.hasAttribute('style')) {
    const styleValue = currentNode.getAttribute('style');
    if (styleValue && /javascript:|data:|vbscript:|file:|about:/gi.test(styleValue)) {
      currentNode.removeAttribute('style');
    }
  }
});

// Mirror the server-side sanitizer configuration
const ALLOWED_TAGS = [
  // Text formatting
  'b',
  'i',
  'em',
  'strong',
  'u',
  's',
  'strike',
  'del',
  // Structure
  'br',
  'p',
  'div',
  'span',
  // Lists
  'ul',
  'ol',
  'li',
  // Headings
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  // Tables
  'table',
  'tr',
  'td',
  'th',
  'thead',
  'tbody',
  'tfoot',
  'caption',
  // Other formatting
  'blockquote',
  'a',
  'code',
  'pre',
  'small',
  'sub',
  'sup',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'class', 'id', 'style'];

export interface HtmlValidationResult {
  isValid: boolean;
  sanitizedHtml: string;
  errors: string[];
  warnings: string[];
}

/**
 * Validates HTML content and provides detailed feedback about what was removed
 */
export function validateHtml(html: string): HtmlValidationResult {
  if (!html.trim()) {
    return {
      isValid: true,
      sanitizedHtml: '',
      errors: [],
      warnings: [],
    };
  }

  // Sanitize with the same config as server
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Disable data attributes for security
    ALLOW_DATA_ATTR: false,
    // Allow safe URLs for href attributes
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });

  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if content was modified (simplified comparison)
  const originalNormalized = html.replace(/\s+/g, ' ').trim();
  const sanitizedNormalized = sanitizedHtml.replace(/\s+/g, ' ').trim();

  if (originalNormalized !== sanitizedNormalized) {
    // Try to identify what was removed
    const forbiddenTags = findForbiddenTags(html);
    const forbiddenAttrs = findForbiddenAttributes(html);

    if (forbiddenTags.length > 0) {
      errors.push(`Forbidden tags detected: ${forbiddenTags.join(', ')}`);
    }

    if (forbiddenAttrs.length > 0) {
      warnings.push(`Forbidden attributes detected: ${forbiddenAttrs.join(', ')}`);
    }

    // Check for dangerous patterns
    if (html.includes('<script') || html.includes('javascript:')) {
      errors.push('Script tags and javascript: URLs are not allowed');
    }

    if (html.includes('onerror') || html.includes('onclick') || html.includes('onload')) {
      errors.push('Event handlers (onclick, onerror, etc.) are not allowed');
    }
  }

  return {
    isValid: errors.length === 0,
    sanitizedHtml,
    errors,
    warnings,
  };
}

function findForbiddenTags(html: string): string[] {
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)/g;
  const foundTags = new Set<string>();
  const forbiddenTags: string[] = [];

  let match;
  while ((match = tagRegex.exec(html)) !== null) {
    const tag = match[1].toLowerCase();
    if (!foundTags.has(tag)) {
      foundTags.add(tag);
      if (!ALLOWED_TAGS.includes(tag)) {
        forbiddenTags.push(`<${tag}>`);
      }
    }
  }

  return forbiddenTags;
}

function findForbiddenAttributes(html: string): string[] {
  const attrRegex = /\s([a-zA-Z-]+)=/g;
  const foundAttrs = new Set<string>();
  const forbiddenAttrs: string[] = [];

  let match;
  while ((match = attrRegex.exec(html)) !== null) {
    const attr = match[1].toLowerCase();
    if (!foundAttrs.has(attr)) {
      foundAttrs.add(attr);
      if (!ALLOWED_ATTR.includes(attr) && !attr.startsWith('data-')) {
        forbiddenAttrs.push(attr);
      }
    }
  }

  return forbiddenAttrs;
}

/**
 * Get a helpful message about what HTML is allowed
 */
export function getAllowedHtmlHelp(): string {
  return `
Allowed tags: ${ALLOWED_TAGS.map((tag) => `<${tag}>`).join(', ')}

Allowed attributes: ${ALLOWED_ATTR.join(', ')}

Examples:
• <p style="color: red;">Colored text</p>
• <strong>Bold text</strong>
• <a href="https://example.com">Links</a>
• <table><tr><td>Tables</td></tr></table>
  `.trim();
}
